import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { axiosInstance } from '../../utils/axios';
import {
  clearPatientWebSession,
  loadPatientWebSession,
  normalizePatientReturnTo,
  savePatientWebSession,
} from '../../utils/patientWebAuth';
import './PatientWebAuthRoutes.scss';

const errorMessages = {
  account_link_required: 'このメールアドレスは既に使用されています。',
  clinical_email_consent_required: '診療メール通知への同意が必要です。',
  email_not_verified: '確認メールを開いてから、もう一度ログインしてください。',
  invalid_credentials: 'メールアドレスまたはパスワードが正しくありません。',
  invalid_email: 'メールアドレスの形式をご確認ください。',
  invalid_or_expired_token: 'リンクの有効期限が切れているか、無効です。',
  invalid_password: 'パスワードは8文字以上で、確認用と一致させてください。',
  invalid_otp_code: '認証コードが正しくありません。',
  otp_expired: '認証コードの有効期限が切れました。もう一度ログインしてください。',
  otp_resend_too_soon: 'しばらく待ってから再送信してください。',
  email_delivery_failed: 'メールを送信できませんでした。時間をおいて再度お試しください。',
  rate_limited: '操作回数が上限に達しました。時間をおいて再度お試しください。',
  terms_not_accepted: '利用規約とプライバシーポリシーへの同意が必要です。',
  validation_failed: '入力内容をご確認ください。',
};

const apiErrorMessage = (error, fallback) => {
  const code = error?.response?.data?.error;
  return errorMessages[code] || fallback;
};

function AuthLayout({ title, subtitle, children }) {
  return (
    <main className="mc-web-auth">
      <section className="mc-web-auth__card">
        <a className="mc-web-auth__brand" href="/">
          <span className="mc-web-auth__brand-mark" aria-hidden="true">＋</span>
          <span>CureNow</span>
        </a>
        <header className="mc-web-auth__header">
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </header>
        {children}
      </section>
    </main>
  );
}

function SubmitButton({ children, disabled, pending }) {
  return (
    <button
      className="mc-web-auth__submit"
      disabled={disabled || pending}
      type="submit"
    >
      {pending ? '送信しています' : children}
    </button>
  );
}

function LoginPage({ onAuthenticated }) {
  const [searchParams] = useSearchParams();
  const storedWebSession = loadPatientWebSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [needsClinicalConsent, setNeedsClinicalConsent] = useState(
    () => Boolean(loadPatientWebSession()?.clinical_email_consent_required),
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [clinicalEmailConsent, setClinicalEmailConsent] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [resendWaitSeconds, setResendWaitSeconds] = useState(0);
  const returnTo = normalizePatientReturnTo(
    searchParams.get('return_to') || storedWebSession?.clinical_email_consent_return_to,
  );
  const passwordResetCompleted = searchParams.get('password_reset') === 'completed';

  useEffect(() => {
    if (resendWaitSeconds <= 0) return undefined;

    const timer = setTimeout(() => setResendWaitSeconds((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendWaitSeconds]);

  const completeLogin = (data) => {
    savePatientWebSession(data);
    if (data.user?.clinical_email_consent_required) {
      setNeedsClinicalConsent(true);
    } else {
      onAuthenticated(returnTo);
    }
  };

  const backToPasswordStep = () => {
    setOtpChallenge(null);
    setOtpCode('');
    setResendWaitSeconds(0);
    setPassword('');
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setUnverified(false);
    setPending(true);
    try {
      const { data } = await axiosInstance.post(
        '/api/medichat/auth/email/login',
        { email, password },
        { skipGlobalError: true },
      );
      if (data.otp_required) {
        setOtpChallenge({
          token: data.challenge_token,
          email: data.email,
          codeLength: data.code_length || 6,
          expiresInMinutes: Math.ceil((data.expires_in || 600) / 60),
        });
        setOtpCode('');
        setResendWaitSeconds(data.resend_available_in || 0);
      } else {
        completeLogin(data);
      }
    } catch (caught) {
      setUnverified(caught?.response?.data?.error === 'email_not_verified');
      setError(apiErrorMessage(caught, 'ログインできませんでした。時間をおいて再度お試しください。'));
    } finally {
      setPending(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setPending(true);
    try {
      const { data } = await axiosInstance.post(
        '/api/medichat/auth/email/login/verify',
        { challenge_token: otpChallenge.token, code: otpCode },
        { skipGlobalError: true },
      );
      completeLogin(data);
    } catch (caught) {
      const code = caught?.response?.data?.error;
      if (code === 'otp_expired') {
        backToPasswordStep();
      } else {
        setOtpCode('');
      }
      setError(apiErrorMessage(caught, '認証できませんでした。時間をおいて再度お試しください。'));
    } finally {
      setPending(false);
    }
  };

  const resendOtp = async () => {
    setError('');
    setNotice('');
    setPending(true);
    try {
      const { data } = await axiosInstance.post(
        '/api/medichat/auth/email/login/resend',
        { challenge_token: otpChallenge.token },
        { skipGlobalError: true },
      );
      setOtpCode('');
      setResendWaitSeconds(data.resend_available_in || 0);
      setNotice('認証コードを再送信しました。');
    } catch (caught) {
      const data = caught?.response?.data;
      if (data?.error === 'otp_expired') {
        backToPasswordStep();
      } else if (data?.resend_available_in) {
        setResendWaitSeconds(data.resend_available_in);
      }
      setError(apiErrorMessage(caught, '認証コードを再送信できませんでした。時間をおいて再度お試しください。'));
    } finally {
      setPending(false);
    }
  };

  const resend = async () => {
    setError('');
    setNotice('');
    setPending(true);
    try {
      await axiosInstance.post(
        '/api/medichat/auth/email/resend-verification',
        { email, return_to: returnTo },
        { skipGlobalError: true },
      );
      setNotice('確認メールを再送信しました。');
    } catch (_) {
      setError('確認メールを再送信できませんでした。時間をおいて再度お試しください。');
    } finally {
      setPending(false);
    }
  };

  const acceptClinicalConsent = async (event) => {
    event.preventDefault();
    setError('');
    setPending(true);
    try {
      const { data } = await axiosInstance.post(
        '/api/medichat/auth/email/clinical-consent',
        {
          terms_accepted: termsAccepted,
          clinical_email_consent: clinicalEmailConsent,
        },
        { skipGlobalError: true },
      );
      savePatientWebSession(data);
      onAuthenticated(returnTo);
    } catch (caught) {
      setError(apiErrorMessage(caught, '同意内容を保存できませんでした。時間をおいて再度お試しください。'));
    } finally {
      setPending(false);
    }
  };

  if (needsClinicalConsent) {
    return (
      <AuthLayout title="通知設定の確認" subtitle="診療に必要なご案内をメールで受け取るため、内容をご確認ください">
        <form className="mc-web-auth__form" onSubmit={acceptClinicalConsent}>
          <label className="mc-web-auth__consent">
            <input
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
              required
              type="checkbox"
            />
            <span>
              <a href="https://www.notion.so/CureNow-2cba4be0fa7080cd8e3ee5b623afb4a7" rel="noreferrer" target="_blank">
                利用規約
              </a>
              と
              <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080bc9233f81808fbb4d0" rel="noreferrer" target="_blank">
                プライバシーポリシー
              </a>
              に同意します
            </span>
          </label>
          <label className="mc-web-auth__consent">
            <input
              checked={clinicalEmailConsent}
              onChange={(event) => setClinicalEmailConsent(event.target.checked)}
              required
              type="checkbox"
            />
            <span>診療内容・予約・決済などの通知を登録メールアドレスへ送信することに同意します</span>
          </label>
          {error && <p className="mc-web-auth__message mc-web-auth__message--error" role="alert">{error}</p>}
          <SubmitButton disabled={!termsAccepted || !clinicalEmailConsent} pending={pending}>
            同意して診察へ進む
          </SubmitButton>
        </form>
      </AuthLayout>
    );
  }

  if (otpChallenge) {
    return (
      <AuthLayout
        title="認証コードの入力"
        subtitle={`${otpChallenge.email || '登録メールアドレス'} 宛に送信した${otpChallenge.codeLength}桁のコードを入力してください`}
      >
        <form className="mc-web-auth__form" onSubmit={verifyOtp}>
          <label>
            <span>認証コード</span>
            <input
              autoComplete="one-time-code"
              autoFocus
              className="mc-web-auth__code-input"
              inputMode="numeric"
              maxLength={otpChallenge.codeLength}
              onChange={(event) => setOtpCode(
                event.target.value
                  .replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xfee0))
                  .replace(/\D/g, '')
                  .slice(0, otpChallenge.codeLength),
              )}
              pattern="[0-9]*"
              required
              type="text"
              value={otpCode}
            />
          </label>
          <p className="mc-web-auth__message">コードの有効期限は{otpChallenge.expiresInMinutes}分です。</p>
          {error && <p className="mc-web-auth__message mc-web-auth__message--error" role="alert">{error}</p>}
          {notice && <p className="mc-web-auth__message mc-web-auth__message--success" role="status">{notice}</p>}
          <SubmitButton disabled={otpCode.length !== otpChallenge.codeLength} pending={pending}>
            認証してログイン
          </SubmitButton>
          <button
            className="mc-web-auth__secondary"
            disabled={pending || resendWaitSeconds > 0}
            onClick={resendOtp}
            type="button"
          >
            {resendWaitSeconds > 0 ? `コードを再送信（${resendWaitSeconds}秒後）` : 'コードを再送信'}
          </button>
        </form>
        <button className="mc-web-auth__single-link mc-web-auth__text-button" onClick={backToPasswordStep} type="button">
          メールアドレスを入力し直す
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="ログイン" subtitle="メールアドレスでCureNowを利用できます">
      <form className="mc-web-auth__form" onSubmit={submit}>
        <label>
          <span>メールアドレス</span>
          <input
            autoComplete="email"
            inputMode="email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@example.com"
            required
            type="email"
            value={email}
          />
        </label>
        <label>
          <span>パスワード</span>
          <input
            autoComplete="current-password"
            minLength={8}
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </label>
        {error && <p className="mc-web-auth__message mc-web-auth__message--error" role="alert">{error}</p>}
        {passwordResetCompleted && !error && (
          <p className="mc-web-auth__message mc-web-auth__message--success" role="status">
            パスワードを変更しました。新しいパスワードでログインしてください。
          </p>
        )}
        {notice && <p className="mc-web-auth__message mc-web-auth__message--success" role="status">{notice}</p>}
        {unverified && (
          <button
            className="mc-web-auth__secondary"
            disabled={pending || !email}
            onClick={resend}
            type="button"
          >
            確認メールを再送信
          </button>
        )}
        <SubmitButton disabled={!email || !password} pending={pending}>ログイン</SubmitButton>
      </form>
      <nav className="mc-web-auth__links" aria-label="アカウント操作">
        <Link to={`/medichat/register?return_to=${encodeURIComponent(returnTo)}`}>新規登録</Link>
        <Link to={`/medichat/forgot-password?return_to=${encodeURIComponent(returnTo)}`}>
          パスワードを忘れた方
        </Link>
      </nav>
    </AuthLayout>
  );
}

function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [clinicalEmailConsent, setClinicalEmailConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const returnTo = normalizePatientReturnTo(searchParams.get('return_to'));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (password !== confirmation) {
      setError('確認用パスワードが一致しません。');
      return;
    }

    setPending(true);
    try {
      await axiosInstance.post(
        '/api/medichat/auth/email/register',
        {
          email,
          password,
          password_confirmation: confirmation,
          return_to: returnTo,
          terms_accepted: termsAccepted,
          clinical_email_consent: clinicalEmailConsent,
        },
        { skipGlobalError: true },
      );
      setSent(true);
    } catch (caught) {
      setError(apiErrorMessage(caught, '登録できませんでした。入力内容をご確認ください。'));
    } finally {
      setPending(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title="確認メールを送信しました" subtitle="メール内のリンクから登録を完了してください">
        <div className="mc-web-auth__success-icon" aria-hidden="true">✓</div>
        <p className="mc-web-auth__message" role="status">
          {email} 宛に確認メールを送信しました。リンクの有効期限は24時間です。
        </p>
        <Link
          className="mc-web-auth__secondary mc-web-auth__secondary--link"
          to={`/medichat/login?return_to=${encodeURIComponent(returnTo)}`}
        >
          ログイン画面へ戻る
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="新規登録" subtitle="メールアドレスとパスワードで登録します">
      <form className="mc-web-auth__form" onSubmit={submit}>
        <label>
          <span>メールアドレス</span>
          <input
            autoComplete="email"
            inputMode="email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@example.com"
            required
            type="email"
            value={email}
          />
        </label>
        <label>
          <span>パスワード（8文字以上）</span>
          <input
            autoComplete="new-password"
            minLength={8}
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </label>
        <label>
          <span>パスワード（確認）</span>
          <input
            autoComplete="new-password"
            minLength={8}
            onChange={(event) => setConfirmation(event.target.value)}
            required
            type="password"
            value={confirmation}
          />
        </label>
        <label className="mc-web-auth__consent">
          <input
            checked={termsAccepted}
            onChange={(event) => setTermsAccepted(event.target.checked)}
            required
            type="checkbox"
          />
          <span>
            <a href="https://www.notion.so/CureNow-2cba4be0fa7080cd8e3ee5b623afb4a7" rel="noreferrer" target="_blank">
              利用規約
            </a>
            と
            <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080bc9233f81808fbb4d0" rel="noreferrer" target="_blank">
              プライバシーポリシー
            </a>
            に同意します
          </span>
        </label>
        <label className="mc-web-auth__consent">
          <input
            checked={clinicalEmailConsent}
            onChange={(event) => setClinicalEmailConsent(event.target.checked)}
            required
            type="checkbox"
          />
          <span>診療内容・予約・決済などの通知を登録メールアドレスへ送信することに同意します</span>
        </label>
        {error && <p className="mc-web-auth__message mc-web-auth__message--error" role="alert">{error}</p>}
        <SubmitButton
          disabled={!email || !password || !confirmation || !termsAccepted || !clinicalEmailConsent}
          pending={pending}
        >
          確認メールを送信
        </SubmitButton>
      </form>
      <Link
        className="mc-web-auth__single-link"
        to={`/medichat/login?return_to=${encodeURIComponent(returnTo)}`}
      >
        すでにアカウントをお持ちの方
      </Link>
    </AuthLayout>
  );
}

function VerifyEmailPage({ onAuthenticated }) {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const token = searchParams.get('token');
  const returnTo = normalizePatientReturnTo(searchParams.get('return_to'));
  const visibleError = token ? error : '確認リンクが無効です。';

  const handleVerification = async (event) => {
    event.preventDefault();
    if (!token) {
      setError('確認リンクが無効です。');
      return;
    }

    setStatus('pending');
    setError('');
    try {
      const { data } = await axiosInstance.post(
        '/api/medichat/auth/email/verify',
        { token, password },
        {
          headers: {
            Accept: 'application/json',
            'X-Medichat-Client': 'patient-web',
          },
          skipGlobalError: true,
        },
      );
      savePatientWebSession(data);
      setStatus('success');
    } catch (requestError) {
      setStatus('idle');
      setError(apiErrorMessage(
        requestError,
        '確認に失敗しました。登録時のパスワードとリンクをご確認ください。',
      ));
    }
  };

  return (
    <AuthLayout
      title={status === 'success' ? '登録が完了しました' : 'メールアドレスの確認'}
      subtitle={status === 'success' ? undefined : '登録時に設定したパスワードを入力してください'}
    >
      {status === 'success' && (
        <>
          <div className="mc-web-auth__success-icon" aria-hidden="true">✓</div>
          <p className="mc-web-auth__message" role="status">メールアドレスを確認しました。</p>
          <button
            className="mc-web-auth__submit"
            onClick={() => onAuthenticated(returnTo)}
            type="button"
          >
            診察を始める
          </button>
        </>
      )}
      {status !== 'success' && (
        <form className="mc-web-auth__form" onSubmit={handleVerification}>
          <label>
            登録時のパスワード
            <input
              autoComplete="current-password"
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          {visibleError && (
            <p className="mc-web-auth__message mc-web-auth__message--error" role="alert">
              {visibleError}
            </p>
          )}
          <SubmitButton disabled={!token || !password} pending={status === 'pending'}>
            メールアドレスを確認
          </SubmitButton>
          <Link
            className="mc-web-auth__single-link"
            to={`/medichat/login?return_to=${encodeURIComponent(returnTo)}`}
          >
            ログイン画面へ戻る
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}

function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const returnTo = normalizePatientReturnTo(searchParams.get('return_to'));

  const submit = async (event) => {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      await axiosInstance.post(
        '/api/medichat/auth/password/forgot',
        { email, return_to: returnTo },
        { skipGlobalError: true },
      );
      setSent(true);
    } catch (_) {
      setError('再設定メールを送信できませんでした。時間をおいて再度お試しください。');
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout title="パスワード再設定" subtitle="登録したメールアドレスへご案内を送信します">
      {sent ? (
        <>
          <div className="mc-web-auth__success-icon" aria-hidden="true">✓</div>
          <p className="mc-web-auth__message" role="status">
            登録状況にかかわらず、受付結果は同じ表示になります。メールをご確認ください。
          </p>
        </>
      ) : (
        <form className="mc-web-auth__form" onSubmit={submit}>
          <label>
            <span>メールアドレス</span>
            <input
              autoComplete="email"
              inputMode="email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
          {error && <p className="mc-web-auth__message mc-web-auth__message--error" role="alert">{error}</p>}
          <SubmitButton disabled={!email} pending={pending}>再設定メールを送信</SubmitButton>
        </form>
      )}
      <Link
        className="mc-web-auth__single-link"
        to={`/medichat/login?return_to=${encodeURIComponent(returnTo)}`}
      >
        ログイン画面へ戻る
      </Link>
    </AuthLayout>
  );
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const token = searchParams.get('token');
  const returnTo = normalizePatientReturnTo(searchParams.get('return_to'));
  const visibleError = token ? error : '再設定リンクが無効です。もう一度メールを送信してください。';

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (password !== confirmation) {
      setError('確認用パスワードが一致しません。');
      return;
    }

    setPending(true);
    try {
      await axiosInstance.post(
        '/api/medichat/auth/password/reset',
        { token, password, password_confirmation: confirmation },
        { skipGlobalError: true },
      );
      navigate(
        `/medichat/login?password_reset=completed&return_to=${encodeURIComponent(returnTo)}`,
        { replace: true },
      );
    } catch (caught) {
      setError(apiErrorMessage(caught, 'パスワードを変更できませんでした。'));
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout title="新しいパスワード" subtitle="8文字以上で設定してください">
      <form className="mc-web-auth__form" onSubmit={submit}>
        <label>
          <span>新しいパスワード</span>
          <input
            autoComplete="new-password"
            minLength={8}
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </label>
        <label>
          <span>新しいパスワード（確認）</span>
          <input
            autoComplete="new-password"
            minLength={8}
            onChange={(event) => setConfirmation(event.target.value)}
            required
            type="password"
            value={confirmation}
          />
        </label>
        {visibleError && (
          <p className="mc-web-auth__message mc-web-auth__message--error" role="alert">
            {visibleError}
          </p>
        )}
        <SubmitButton disabled={!token || !password || !confirmation} pending={pending}>
          パスワードを変更
        </SubmitButton>
        {!token && (
          <Link className="mc-web-auth__single-link" to="/medichat/forgot-password">
            再設定メールをもう一度送る
          </Link>
        )}
      </form>
    </AuthLayout>
  );
}

function LogoutPage() {
  const navigate = useNavigate();
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    let active = true;
    const logout = async () => {
      try {
        await axiosInstance.post(
          '/api/medichat/auth/logout',
          {},
          { skipGlobalError: true },
        );
      } finally {
        clearPatientWebSession();
        if (active) setComplete(true);
      }
    };
    logout();
    return () => { active = false; };
  }, []);

  return (
    <AuthLayout title={complete ? 'ログアウトしました' : 'ログアウトしています'}>
      {complete ? (
        <button className="mc-web-auth__submit" onClick={() => navigate('/medichat/login')} type="button">
          ログイン画面へ
        </button>
      ) : (
        <div className="mc-web-auth__spinner" aria-label="処理中" role="status" />
      )}
    </AuthLayout>
  );
}

export default function PatientWebAuthRoutes({ onAuthenticated }) {
  return (
    <Routes>
      <Route path="/medichat/login" element={<LoginPage onAuthenticated={onAuthenticated} />} />
      <Route path="/medichat/register" element={<RegisterPage />} />
      <Route path="/medichat/verify-email" element={<VerifyEmailPage onAuthenticated={onAuthenticated} />} />
      <Route path="/medichat/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/medichat/reset-password" element={<ResetPasswordPage />} />
      <Route path="/medichat/logout" element={<LogoutPage />} />
      <Route path="*" element={<Navigate replace to="/medichat/login" />} />
    </Routes>
  );
}
